import React, { useState, useEffect } from "react"
import moment from "moment"

import CatchModal from "~/library/CatchModal"
import { formatCurrency } from "~/utils/helpers"
import Sponsorship from "~/types/Sponsorship"
import PaymentItem from "~/types/PaymentItem"

import getAppConfig from "~/utils/getAppConfig"
import { apiPayments } from "~/routes"
import { useToast } from "~/utils/Toast"

interface PaymentModalProps {
  onClose: () => void
  onSuccess: () => void
  sponsorshipId: string
  sponsorship: Sponsorship
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  onClose,
  onSuccess,
  sponsorshipId,
  sponsorship,
}) => {
  const toast = useToast()
  const { venueSlug } = getAppConfig()
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const initialItems: PaymentItem[] = []

    initialItems.push({
      description: `${sponsorship.company.name} (${sponsorship.area.name})`,
      quantity: 1,
      amountCents: sponsorship.price?.cents || 0,
    })

    setPaymentItems(initialItems)
  }, [sponsorship])

  const handleCreateInvoice = () => {
    if (paymentItems.filter((item) => !item._destroy).length === 0) {
      toast("At least one payment item is required", "danger")
      return
    }

    setIsSubmitting(true)

    const req = apiPayments.create({
      data: {
        venueSlug,
        payableId: sponsorshipId,
        payableType: "sponsorship",
        payment: {
          amountCents: calculateTotal(),
          paymentItemsAttributes: paymentItems.filter((item) => !item._destroy),
        },
      },
    })
    req
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch((error) => {
        console.error("Failed to create invoice:", error)
        toast(error.body.errors.join(", "), "danger")
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const addPaymentItem = () => {
    setPaymentItems([
      ...paymentItems,
      {
        description: "",
        quantity: 1,
        amountCents: 0,
      },
    ])
  }

  const removePaymentItem = (index: number) => {
    const updatedItems = [...paymentItems]
    if (updatedItems[index].id) {
      updatedItems[index]._destroy = true
    } else {
      updatedItems.splice(index, 1)
    }
    setPaymentItems(updatedItems)
  }

  const updatePaymentItem = (index: number, field: keyof PaymentItem, value: any) => {
    const updatedItems = [...paymentItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    setPaymentItems(updatedItems)
  }

  const calculateTotal = () => {
    return paymentItems
      .filter((item) => !item._destroy)
      .reduce((total, item) => total + item.quantity * item.amountCents, 0)
  }

  const renderCompanyInfo = () => {
    const company = sponsorship?.company
    if (!company) {
      return (
        <div className="alert alert-warning">
          No company information available for this sponsorship.
        </div>
      )
    }

    return (
      <div className="row">
        <div className="col-12 mb-3">
          <h6 className="text-muted mb-2">BILL TO</h6>
          <div>
            <strong>{company.name}</strong>
            <br />
            {company.contactPerson && <span className="text-muted">{company.contactPerson}</span>}
            <br />
            {company.email && <span className="text-muted">{company.email}</span>}
            <br />
            {company.phone && <span className="text-muted">{company.phone}</span>}
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentItems = () => {
    const visibleItems = paymentItems.filter((item) => !item._destroy)

    return (
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Subtotal</th>
              <th style={{ width: "50px" }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item) => {
              const actualIndex = paymentItems.indexOf(item)
              return (
                <tr key={actualIndex} className="align-middle">
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "description", e.target.value)
                      }
                      placeholder="Campaign Info"
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "quantity", parseInt(e.target.value) || 1)
                      }
                      min="1"
                      style={{ width: "80px" }}
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.amountCents / 100}
                      onChange={(e) =>
                        updatePaymentItem(
                          actualIndex,
                          "amountCents",
                          Math.round((parseFloat(e.target.value) || 0) * 100)
                        )
                      }
                      step="1"
                      min="0"
                      style={{ width: "100px" }}
                      placeholder="0.00"
                      required
                    />
                  </td>
                  <td>
                    {formatCurrency({
                      cents: item.quantity * item.amountCents,
                      currency_iso: "USD",
                    })}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePaymentItem(actualIndex)}
                      disabled={visibleItems.length === 1}
                      title="Remove item"
                    >
                      x
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addPaymentItem}
                >
                  + Add Item
                </button>
              </td>
              <th>{formatCurrency({ cents: calculateTotal(), currency_iso: "USD" })}</th>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <CatchModal isOpen={true} onClose={onClose} title="Create Invoice" size="lg">
      <div className="space-y-4">
        {/* Invoice Header */}
        <div className="row mb-4">
          <div className="col-6">
            <h6 className="text-muted mb-2">INVOICE FOR</h6>
            <div>
              <strong>Sponsorship: {sponsorship?.area?.name}</strong>
              <br />
              <span className="text-muted">
                {moment(sponsorship?.startOn).format("MMMM Do, YYYY")} -{" "}
                {moment(sponsorship?.endOn).format("MMMM Do, YYYY")}
              </span>
              <br />
              {sponsorship?.areaType && (
                <span
                  className={`badge badge-sm ${
                    sponsorship.areaType === "physical" ? "bg-primary" : "bg-info"
                  }`}
                >
                  {sponsorship.areaType === "physical" ? "Physical" : "Digital"}
                </span>
              )}
            </div>
          </div>
          <div className="col-6">{renderCompanyInfo()}</div>
        </div>

        {/* Invoice Items */}
        <div>
          <h6 className="text-muted mb-3">ITEMS TO INVOICE</h6>
          {renderPaymentItems()}
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 mt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary ms-auto">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateInvoice}
            className="btn btn-primary"
            disabled={
              isSubmitting ||
              !sponsorship?.company ||
              paymentItems.filter(
                (item) => !item._destroy && item.description && item.amountCents > 0
              ).length === 0
            }
          >
            {isSubmitting ? "Creating..." : "Create Invoice"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default PaymentModal
